import { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { ThemeProvider } from "@/context/ThemeContext";

import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import VerifyCertificate from "@/pages/VerifyCertificate";
import PublicOnboarding from "@/pages/public/Onboarding";
import InspectorApp from "@/pages/InspectorApp";

import ClientDashboard from "@/pages/client/ClientDashboard";
import NewRequest from "@/pages/client/NewRequest";

import InspectorDashboard from "@/pages/inspector/InspectorDashboard";
import ChecklistPage from "@/pages/inspector/ChecklistPage";

import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminUsers from "@/pages/admin/AdminUsers";
import AdminRubros from "@/pages/admin/AdminRubros";
import AdminPricing from "@/pages/admin/AdminPricing";
import AdminSettingsPricing from "@/pages/admin/AdminSettingsPricing";
import AdminPackages from "@/pages/admin/AdminPackages";
import AdminPayments from "@/pages/admin/AdminPayments";
import PaymentSuccess from "@/pages/PaymentSuccess";
import PaymentCancel from "@/pages/PaymentCancel";
import AdminChecklistMaster from "@/pages/admin/AdminChecklistMaster";
import AdminCriticalFindings from "@/pages/admin/AdminCriticalFindings";
import AdminRiskRadar from "@/pages/admin/AdminRiskRadar";
import AdminCertifications from "@/pages/admin/AdminCertifications";
import AdminDistintivos from "@/pages/admin/AdminDistintivos";
import AdminExpiringCerts from "@/pages/admin/AdminExpiringCerts";
import AdminInspections from "@/pages/admin/AdminInspections";
import AdminTeamMap from "@/pages/admin/AdminTeamMap";
import AdminCatalog from "@/pages/admin/AdminCatalog";
import AdminContacts from "@/pages/admin/AdminContacts";
import AdminDiagnostics from "@/pages/admin/AdminDiagnostics";
import AdminEvidencias from "@/pages/admin/AdminEvidencias";
import AdminCiclos from "@/pages/admin/AdminCiclos";
import AdminCorporativos from "@/pages/admin/AdminCorporativos";
import PortalCorporativo from "@/pages/corporativo/PortalCorporativo";
import AdminSettingsCertifications from "@/pages/admin/AdminSettingsCertifications";import AdminCitationExplanations from "@/pages/admin/AdminCitationExplanations";import AdminClients from "@/pages/admin/AdminClients";
import AdminInspectors from "@/pages/admin/AdminInspectors";
import InspectorDetail from "@/pages/admin/InspectorDetail";
import InspectionForm from "@/pages/admin/InspectionForm";
import ClientDetail from "@/pages/admin/ClientDetail";
import Catalog, { CatalogDetail } from "@/pages/Catalog";
import Profile from "@/pages/Profile";
import usePWAStandalone from "@/hooks/usePWAStandalone";

function ProtectedRoute({ role, children }) {
    const { user } = useAuth();
    if (!user) return <Navigate to="/login" replace />;
    if (user.role !== role) return <Navigate to={`/${user.role}`} replace />;
    return children;
}

function AuthenticatedRoute({ children }) {
    const { user } = useAuth();
    if (!user) return <Navigate to="/login" replace />;
    return children;
}

/**
 * PWA Gate: si la app corre en modo standalone (instalada como app nativa),
 * fuerza que solo el rol `inspector` pueda usarla. Otros roles son enviados a /app
 * (que los deslogueará automáticamente).
 */
function PWAStandaloneGate() {
    const { user, logout } = useAuth();
    const isStandalone = usePWAStandalone();
    const navigate = useNavigate();
    const location = useLocation();

    // 🔐 Seguridad: al abrir la PWA (nueva sesión), forzar logout para exigir contraseña
    useEffect(() => {
        if (!isStandalone) return;
        const alreadyStarted = sessionStorage.getItem('pwa_session_started');
        if (!alreadyStarted) {
            sessionStorage.setItem('pwa_session_started', '1');
            // Solo cerrar sesión si había una guardada (evita loop en la propia página /app)
            if (user) {
                logout();
                navigate('/app', { replace: true });
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isStandalone]);

    useEffect(() => {
        if (!isStandalone) return;
        // Si está autenticado con un rol distinto a inspector → logout y a /app
        if (user && user.role !== 'inspector') {
            logout();
            navigate('/app', { replace: true });
            return;
        }
        // Si NO está autenticado, mandar a /app en lugar de landing/login
        if (!user && !['/app', '/login'].includes(location.pathname) && !location.pathname.startsWith('/verificar/')) {
            navigate('/app', { replace: true });
        }
    }, [isStandalone, user, location.pathname, logout, navigate]);

    return null;
}

function App() {
    return (
        <div className="App">
            <ThemeProvider>
                <AuthProvider>
                    <Toaster position="top-right" richColors />
                    <BrowserRouter>
                        <PWAStandaloneGate />
                        <Routes>
                            <Route path="/" element={<Landing />} />
                            <Route path="/login" element={<Login />} />
                            <Route path="/register" element={<Register />} />
                            <Route path="/verificar/:token" element={<VerifyCertificate />} />
                            <Route path="/onboarding/:token" element={<PublicOnboarding />} />
                            <Route path="/profile" element={<AuthenticatedRoute><Profile /></AuthenticatedRoute>} />
                            <Route path="/payment/success" element={<PaymentSuccess />} />
                            <Route path="/payment/cancel" element={<PaymentCancel />} />
                            <Route path="/app" element={<InspectorApp />} />

                            <Route path="/client" element={<ProtectedRoute role="client"><ClientDashboard /></ProtectedRoute>} />
                            <Route path="/client/requests/new" element={<ProtectedRoute role="client"><NewRequest /></ProtectedRoute>} />

                            <Route path="/provider" element={<ProtectedRoute role="provider"><ClientDashboard /></ProtectedRoute>} />
                            <Route path="/provider/requests/new" element={<ProtectedRoute role="provider"><NewRequest /></ProtectedRoute>} />

                            <Route path="/inspector" element={<ProtectedRoute role="inspector"><InspectorDashboard /></ProtectedRoute>} />
                            <Route path="/inspector/checklist/:id" element={<ProtectedRoute role="inspector"><ChecklistPage /></ProtectedRoute>} />
                            <Route path="/inspector/inspections/:id/form" element={<ProtectedRoute role="inspector"><InspectionForm /></ProtectedRoute>} />

                            <Route path="/admin" element={<ProtectedRoute role="admin"><AdminDashboard /></ProtectedRoute>} />
                            <Route path="/admin/users" element={<ProtectedRoute role="admin"><AdminUsers /></ProtectedRoute>} />
                            <Route path="/admin/rubros" element={<ProtectedRoute role="admin"><AdminRubros /></ProtectedRoute>} />
                            <Route path="/admin/pricing" element={<ProtectedRoute role="admin"><AdminPricing /></ProtectedRoute>} />
                            <Route path="/admin/settings/pricing" element={<ProtectedRoute role="admin"><AdminSettingsPricing /></ProtectedRoute>} />
                            <Route path="/admin/packages" element={<ProtectedRoute role="admin"><AdminPackages /></ProtectedRoute>} />
                            <Route path="/admin/payments" element={<ProtectedRoute role="admin"><AdminPayments /></ProtectedRoute>} />
                            <Route path="/admin/checklist-master" element={<ProtectedRoute role="admin"><AdminChecklistMaster /></ProtectedRoute>} />
                            <Route path="/admin/critical-findings" element={<ProtectedRoute role="admin"><AdminCriticalFindings /></ProtectedRoute>} />
                            <Route path="/admin/risk-radar" element={<ProtectedRoute role="admin"><AdminRiskRadar /></ProtectedRoute>} />
                            <Route path="/admin/certifications" element={<ProtectedRoute role="admin"><AdminCertifications /></ProtectedRoute>} />
                            <Route path="/admin/certifications/expiring" element={<ProtectedRoute role="admin"><AdminExpiringCerts /></ProtectedRoute>} />
                            <Route path="/admin/distintivos" element={<ProtectedRoute role="admin"><AdminDistintivos /></ProtectedRoute>} />
                            <Route path="/admin/inspections" element={<ProtectedRoute role="admin"><AdminInspections /></ProtectedRoute>} />
                            <Route path="/admin/team-map" element={<ProtectedRoute role="admin"><AdminTeamMap /></ProtectedRoute>} />
                            <Route path="/admin/catalog" element={<ProtectedRoute role="admin"><AdminCatalog /></ProtectedRoute>} />
                            <Route path="/admin/contacts" element={<ProtectedRoute role="admin"><AdminContacts /></ProtectedRoute>} />
                            <Route path="/corporativo" element={<ProtectedRoute role="client"><PortalCorporativo /></ProtectedRoute>} />
                    <Route path="/admin/corporativos" element={<ProtectedRoute role="admin"><AdminCorporativos /></ProtectedRoute>} />
                    <Route path="/admin/ciclos" element={<ProtectedRoute role="admin"><AdminCiclos /></ProtectedRoute>} />
                    <Route path="/admin/evidencias" element={<ProtectedRoute role="admin"><AdminEvidencias /></ProtectedRoute>} />
                    <Route path="/admin/diagnostics" element={<ProtectedRoute role="admin"><AdminDiagnostics /></ProtectedRoute>} />
                            <Route path="/admin/settings/certifications" element={<ProtectedRoute role="admin"><AdminSettingsCertifications /></ProtectedRoute>} />
                            <Route path="/admin/settings/citations" element={<ProtectedRoute role="admin"><AdminCitationExplanations /></ProtectedRoute>} />
                            <Route path="/admin/clients" element={<ProtectedRoute role="admin"><AdminClients /></ProtectedRoute>} />
                            <Route path="/admin/clients/:id" element={<ProtectedRoute role="admin"><ClientDetail /></ProtectedRoute>} />
                            <Route path="/admin/inspectors" element={<ProtectedRoute role="admin"><AdminInspectors /></ProtectedRoute>} />
                            <Route path="/admin/inspectors/:id" element={<ProtectedRoute role="admin"><InspectorDetail /></ProtectedRoute>} />
                            <Route path="/admin/inspections/:id/form" element={<ProtectedRoute role="admin"><InspectionForm /></ProtectedRoute>} />

                            <Route path="/catalogo/clientes" element={<Catalog />} />
                            <Route path="/catalogo/proveedores" element={<Catalog />} />
                            <Route path="/catalogo/empresa/:id" element={<CatalogDetail />} />

                            <Route path="*" element={<Navigate to="/" />} />
                        </Routes>
                    </BrowserRouter>
                </AuthProvider>
            </ThemeProvider>
        </div>
    );
}

export default App;
